import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import BugsnagPerformance from "@bugsnag/browser-performance";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import Bugsnag from "@bugsnag/js";
import { getUserProfile } from "@platform/utils/host.util";

import { queryClient } from "@config/queryClient";
import { REACT_QUERY_ERROR } from "@config/constants";
import { isIgnorableBugsnagEvent } from "@config/bugsnagFilters";
import MainScreenManager from "@components/ScreenManager/MainScreenManager";
import ErrorFallback from "./ErrorFallback";
import usePlatformStore from "@store/usePlatformStore";

const isLocal = process.env.REACT_IS_LOCAL === "true";
const environmentName = process.env.REACT_ENVIRONMENT_NAME;
const enabledReleaseStages = ["local", "dev", "uat", "staging", "prod"];

// Mirrors the deploy workflow's --app-version so runtime events match
// uploaded sourcemaps. REACT_APP_VERSION holds:
//   - github.ref_name on staging (e.g. "release/v1.2.3" -> "v1.2.3-staging")
//   - github.sha on dev/uat/prod (opaque, no prefix to strip)
//   - unset locally -> "0.0.0-<env>" fallback
const bugsnagAppVersion: string | undefined = (() => {
  if (!environmentName) return undefined;
  const raw = process.env.REACT_APP_VERSION;
  const tag = raw ? raw.replace(/^(release|hotfix)\//, "") : "0.0.0";
  return `${tag}-${environmentName}`;
})();

if (!isLocal && !environmentName) {
  // eslint-disable-next-line no-console
  console.warn("[bugsnag] REACT_ENVIRONMENT_NAME missing; sourcemaps won't link");
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  FallbackComponent?: React.ComponentType<{
    error: Error;
    info: { componentStack: string };
    clearError: () => void;
  }>;
  onError?: (error: Error, componentStack: string) => void;
}

let ErrorBoundary: React.ComponentType<ErrorBoundaryProps> = ({ children }) => <>{children}</>;

if (!isLocal) {
  Bugsnag.start({
    apiKey: process.env.REACT_BUGSNAG_API_KEY,
    appVersion: bugsnagAppVersion,
    plugins: [new BugsnagPluginReact()],
    releaseStage: environmentName,
    enabledReleaseStages,
    endpoints: {
      notify: process.env.REACT_BUGSNAG_ENDPOINT + "/bugsnag/notify/",
      sessions: process.env.REACT_BUGSNAG_ENDPOINT + "/bugsnag/sessions/",
    },
    autoTrackSessions: false,
    onError: (event) => {
      // Drop known-noise events before any further processing
      if (isIgnorableBugsnagEvent(event)) return false;

      // Workaround fixing Bugsnag logging in the Chrome Extension
      // https://docs.bugsnag.com/platforms/javascript/faq/#how-can-i-get-error-reports-from-browser-extensions
      if (usePlatformStore.getState().isChrome()) {
        const firstError = event.errors?.[0];
        const stack = firstError?.stacktrace;

        if (firstError && Array.isArray(stack)) {
          firstError.stacktrace = stack.map((frame: any) => {
            if (!frame || typeof frame.file !== "string") return frame;
            return {
              ...frame,
              file: frame.file.replace(/chrome-extension:/g, "chrome_extension:"),
            };
          });
        }
      }

      const profile = getUserProfile();
      if (profile?.emailAddress) {
        const { emailAddress, displayName, timeZone } = profile;
        event.setUser(emailAddress, emailAddress, displayName ?? emailAddress);
        if (timeZone) event.addMetadata("timeZone", { timeZone });
      }

      const errorClass = event.errors?.[0]?.errorClass;

      if (errorClass === REACT_QUERY_ERROR) {
        event.severity = "error";
        return true;
      }

      // Filter out warning-level events
      if (event.severity === "warning" || errorClass === "Warning") {
        return false; // prevent sending this event to BugSnag
      }
    },
  });

  Bugsnag.startSession();

  BugsnagPerformance.start({
    apiKey: process.env.REACT_BUGSNAG_API_KEY,
    appVersion: bugsnagAppVersion,
    releaseStage: environmentName,
    enabledReleaseStages,
    endpoint: process.env.REACT_BUGSNAG_ENDPOINT + "/bugsnag/metrics/",
  });

  const bugsnagReact = Bugsnag.getPlugin("react");
  if (bugsnagReact) {
    ErrorBoundary = bugsnagReact.createErrorBoundary(
      React
    ) as unknown as React.ComponentType<ErrorBoundaryProps>;
  }
}

export default function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <MainScreenManager />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
